<!--
  Карточка одного файла в AttachmentUploader.

  Показывает превью или иконку файла, статус загрузки, имя, размер и кнопку
  удаления. Сама карточка не загружает файл и не ходит в API: она только
  отображает attachment и сообщает родителю о действиях пользователя.
-->
<template>
  <div
    class="attachment-item"
    :class="{ 'attachment-item--compact': compact }"
  >
    <div
      class="attachment-preview-container"
      @click.stop="$emit('preview', attachment)"
    >
      <img
        v-if="isImage(attachment) && attachment.preview"
        :src="attachment.preview"
        class="attachment-preview"
      />
      <div v-else class="file-icon-container">
        <div
          class="file-icon"
          :class="getFileIconClass(attachment)"
        >
          <span class="file-extension">
            {{ getFileExtension(attachment) }}
          </span>
        </div>
        <div class="file-type">
          {{ getFileTypeName(attachment) }}
        </div>
      </div>
    </div>

    <div
      class="attachment-progress"
      v-if="attachment.uploading || attachment.failed || attachment.uploaded"
      :class="{
        'attachment-progress--failed': attachment.failed,
        'attachment-progress--uploaded': attachment.uploaded,
      }"
    >
      <span class="progress-text">
        {{ statusLabel }}
      </span>
      <div v-if="attachment.uploading" class="progress-track">
        <div
          class="progress-bar"
          :style="{ width: attachment.progress + '%' }"
        ></div>
      </div>
    </div>

    <div v-if="!compact" class="attachment-info">
      <div class="attachment-name" :title="attachment.name">
        {{ truncateFileName(attachment.name) }}
      </div>
      <div class="attachment-size">
        {{ formatFileSize(attachment.size) }}
      </div>
    </div>

    <button
      @click.stop.prevent="$emit('remove', attachment)"
      class="remove-attachment-btn"
      :disabled="attachment.uploading"
      title="Удалить файл"
    >
      <AppIcon name="close" :size="14" />
    </button>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";

import {
  isImage,
  getFileIconClass,
  getFileExtension,
  getFileTypeName,
  truncateFileName,
  formatFileSize,
} from "@/utils/fileUtils";

export default {
  name: "AttachmentUploadItem",
  components: {
    AppIcon,
  },
  props: {
    attachment: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["preview", "remove"],
  computed: {
    statusLabel() {
      if (this.attachment.failed) return "Ошибка";

      if (this.attachment.uploaded) return "Загружен";

      return "Загрузка";
    },
  },
  methods: {
    isImage,
    getFileIconClass,
    getFileExtension,
    getFileTypeName,
    truncateFileName,
    formatFileSize,
  },
};
</script>

<style scoped>
.attachment-item {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;
  background-color: white;
}

.attachment-item--compact {
  width: 72px;
  min-height: 78px;
  height: 78px;
  padding: 6px;
  flex-direction: column;
  justify-content: flex-start;
}

.attachment-item:hover {
  border-color: var(--color-green);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.attachment-preview-container {
  position: relative;
  width: 100%;
  height: 108px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
  cursor: pointer;
}

.attachment-item--compact .attachment-preview-container {
  width: 54px;
  height: 54px;
  flex-basis: 54px;
  margin-right: 0;
  margin-bottom: 5px;
}

.attachment-preview {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.file-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.file-icon {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 5px;
}

.attachment-item--compact .file-icon {
  width: 42px;
  height: 42px;
}

.file-icon.image-icon {
  background-color: #e3f2fd;
  color: #1976d2;
}

.file-icon.pdf-icon {
  background-color: #ffebee;
  color: #f44336;
}

.file-icon.word-icon {
  background-color: #e8f5e9;
  color: #4caf50;
}

.file-icon.excel-icon {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.file-icon.archive-icon {
  background-color: #f3e5f5;
  color: #9c27b0;
}

.file-icon.text-icon {
  background-color: #e0f7fa;
  color: #00bcd4;
}

.file-icon.default-icon {
  background-color: #f5f5f5;
  color: #616161;
}

.file-extension {
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  background-color: white;
  padding: 2px 6px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}

.file-type {
  font-size: 11px;
  color: #666;
  text-align: center;
  margin-top: 10px;
}

.attachment-item--compact .file-type {
  display: none;
}

.attachment-info {
  width: 100%;
  text-align: center;
}

.attachment-name {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 3px;
  font-weight: 500;
}

.attachment-size {
  font-size: 10px;
  color: #666;
}

.attachment-progress {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  margin: 0 0 9px;
}

.attachment-progress--failed {
  width: auto;
  align-self: center;
  margin: 0 0 9px;
  padding: 3px 8px;
  border-radius: 999px;
  background-color: #fee2e2;
  box-shadow: none;
}

.attachment-progress--uploaded {
  width: auto;
  align-self: center;
  margin: 0 0 9px;
  padding: 3px 8px;
  border-radius: 999px;
  background-color: #ecfdf5;
}

.progress-text {
  position: static;
  padding: 2px 6px;
  border-radius: 999px;
  background-color: #eef8f6;
  color: #047866;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  white-space: nowrap;
}

.progress-track {
  overflow: hidden;
  width: 100%;
  height: 3px;
  border-radius: 999px;
  background-color: #e5e7eb;
}

.progress-bar {
  height: 100%;
  background-color: var(--color-green);
  border-radius: inherit;
  transition: width 0.25s ease;
}

.attachment-progress--failed .progress-text {
  padding: 0;
  background-color: transparent;
  color: #b91c1c;
}

.attachment-progress--uploaded .progress-text {
  padding: 0;
  background-color: transparent;
  color: #047857;
}

.attachment-item--compact .attachment-progress {
  position: static;
  width: 54px;
  margin: 0;
}

.attachment-item--compact .progress-track {
  height: 3px;
}

.attachment-item--compact .progress-text {
  display: none;
}

.attachment-item--compact.attachment-progress--uploaded {
  display: none;
}

.remove-attachment-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, background-color 0.2s;
}

.attachment-item--compact .remove-attachment-btn {
  top: -7px;
  right: -7px;
  transform: none;
}

.attachment-item:hover .remove-attachment-btn {
  opacity: 1;
}

.remove-attachment-btn:hover {
  background-color: #dc2626;
}

.remove-attachment-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
